import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Users,
  FileText,
  Phone,
  Mail,
  MapPin,
  Hash,
  User as UserIcon,
  Edit,
  Save,
  X,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/toaster.jsx';
import { fetchClient, updateClient } from './Services/clientsService';
import { TIPO_EMPRESA_OPTIONS, ESTADO_OPTIONS } from './utils/clientsColumns';
import ContactsSection from './sections/ContactsSection';
import DocumentsSection from './sections/DocumentsSection';
import { cn } from '../../lib/utils';

const ESTADO_BADGE = {
  Activo: 'success',
  Inactivo: 'secondary',
};

// Placeholders — se completarán cuando agreguemos los campos al backend.
const REGIMEN_TRIBUTARIO_OPTIONS = [
  { value: 'Simplificado', label: 'Simplificado' },
  { value: 'Común', label: 'Común' },
  { value: 'Especial', label: 'Especial' },
  { value: 'Gran Contribuyente', label: 'Gran Contribuyente' },
  { value: 'Autorretenedor', label: 'Autorretenedor' },
];

const SECTIONS = [
  { id: 'general', label: 'Información General', icon: Building2 },
  { id: 'contacts', label: 'Contactos', icon: Users },
  { id: 'documents', label: 'Documentos', icon: FileText },
];

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('general');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchClient(id);
      if (!r?.success) {
        toast.error('No se pudo cargar el cliente', r?.message);
        navigate('/clients', { replace: true });
        return;
      }
      setClient(r.data);
    } catch (e) {
      toast.error('Error al cargar el cliente', e?.message);
      navigate('/clients', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Cargando cliente…
      </div>
    );
  }

  if (!client) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mx-auto mb-3" />
          Cliente no encontrado.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <ClientHeader client={client} onBack={() => navigate('/clients')} />

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
        <SidebarNav active={activeSection} onChange={setActiveSection} />

        <main className="min-w-0">
          {activeSection === 'general' && (
            <GeneralInfoSection
              client={client}
              onClientUpdated={setClient}
            />
          )}
          {activeSection === 'contacts' && (
            <ContactsSection
              clienteId={client._id}
              clienteNombre={client.nombre}
            />
          )}
          {activeSection === 'documents' && (
            <DocumentsSection
              clienteId={client._id}
              clienteNombre={client.nombre}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function ClientHeader({ client, onBack }) {
  const initial = (client?.nombre || '?').trim().charAt(0).toUpperCase();
  return (
    <Card>
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center gap-3 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="Volver"
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg md:text-xl font-semibold shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground truncate">
              {client.nombre}
            </h1>
            {client.tipoEmpresa && (
              <p className="text-xs md:text-sm text-muted-foreground">
                {client.tipoEmpresa}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <Badge variant={ESTADO_BADGE[client.estado] || 'secondary'}>
              {client.estado}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SidebarNav({ active, onChange }) {
  return (
    <Card className="md:sticky md:top-4">
      <CardContent className="p-2">
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange(s.id)}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
                  'flex-1 md:flex-none md:w-full justify-start',
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{s.label}</span>
              </button>
            );
          })}
        </nav>
      </CardContent>
    </Card>
  );
}

function GeneralInfoSection({ client, onClientUpdated }) {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(() => buildFormData(client));
  const [saving, setSaving] = useState(false);

  // Si cambia el cliente (por reload), re-sincroniza el form.
  useEffect(() => {
    if (!isEditing) setFormData(buildFormData(client));
  }, [client, isEditing]);

  const dirty = useMemo(
    () => isFormDirty(formData, client),
    [formData, client],
  );

  const handleStartEdit = () => {
    setFormData(buildFormData(client));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(buildFormData(client));
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        estado: formData.estado,
        tipoEmpresa: formData.tipoEmpresa,
        nombre: formData.nombre.trim(),
        nombreContacto: formData.nombreContacto?.trim() || undefined,
        nit: formData.nit.trim(),
        correo: formData.correo.trim(),
        telefono: formData.telefono?.trim() || undefined,
        direccion: formData.direccion?.trim() || undefined,
      };
      const r = await updateClient(client._id, payload);
      if (!r?.success) {
        toast.error('No se pudo guardar', r?.message);
        return;
      }
      onClientUpdated?.(r.data);
      toast.success('Cliente actualizado', payload.nombre);
      setIsEditing(false);
    } catch (e) {
      toast.error('Error al guardar', e?.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setFormData((p) => ({ ...p, [key]: value }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <SectionCard
        title="Identificación Empresarial"
        icon={Building2}
        action={
          isEditing ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="h-3.5 w-3.5" />
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Guardar
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={handleStartEdit}>
              <Edit className="h-3.5 w-3.5" />
              Editar
            </Button>
          )
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field id="tipoEmpresa" label="Tipo de empresa" required>
            {isEditing ? (
              <EditableSelect
                value={formData.tipoEmpresa}
                onChange={(v) => setField('tipoEmpresa', v)}
                options={TIPO_EMPRESA_OPTIONS}
                placeholder="Seleccionar…"
              />
            ) : (
              <ReadonlySelect value={client.tipoEmpresa} placeholder="—" />
            )}
          </Field>
          <Field id="estado" label="Estado" required>
            {isEditing ? (
              <EditableSelect
                value={formData.estado}
                onChange={(v) => setField('estado', v)}
                options={ESTADO_OPTIONS}
              />
            ) : (
              <ReadonlySelect value={client.estado} placeholder="—" />
            )}
          </Field>
          <Field
            id="regimenTributario"
            label="Régimen tributario"
            badge="Próximamente"
          >
            <ReadonlySelect
              value={client.regimenTributario}
              placeholder="Seleccionar…"
              options={REGIMEN_TRIBUTARIO_OPTIONS}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field id="nombre" label="Nombre / Razón social" required>
            {isEditing ? (
              <EditableInput
                value={formData.nombre}
                onChange={(v) => setField('nombre', v)}
                placeholder="Razón social o nombre comercial"
                icon={Building2}
              />
            ) : (
              <ReadonlyInput
                value={client.nombre}
                placeholder="Razón social o nombre comercial"
              />
            )}
          </Field>
          <Field id="nombreContacto" label="Nombre contacto">
            {isEditing ? (
              <EditableInput
                value={formData.nombreContacto}
                onChange={(v) => setField('nombreContacto', v)}
                placeholder="Representante"
                icon={UserIcon}
              />
            ) : (
              <ReadonlyInput
                value={client.nombreContacto}
                placeholder="Representante"
              />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Field id="nit" label="NIT" required>
            {isEditing ? (
              <EditableInput
                value={formData.nit}
                onChange={(v) => setField('nit', v)}
                placeholder="900123456"
                icon={Hash}
              />
            ) : (
              <ReadonlyInput value={client.nit} placeholder="900123456" />
            )}
          </Field>
          <Field id="digitoVerificacion" label="Dígito" badge="Próximamente">
            <ReadonlyInput
              value={client.digitoVerificacion}
              placeholder="—"
              maxWidth="w-24"
            />
          </Field>
          <Field id="codigoCIIU" label="Código CIIU" badge="Próximamente">
            <ReadonlyInput
              value={client.codigoCIIU}
              placeholder="—"
              maxWidth="w-32"
            />
          </Field>
        </div>

        <div className="mt-4">
          <Field
            id="actividadEconomica"
            label="Actividad económica"
            badge="Próximamente"
          >
            <ReadonlyTextarea
              value={client.actividadEconomica}
              placeholder="Descripción de la actividad económica"
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Contacto y Ubicación" icon={Phone}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="correo" label="Correo electrónico" required>
            {isEditing ? (
              <EditableInput
                type="email"
                value={formData.correo}
                onChange={(v) => setField('correo', v)}
                placeholder="correo@empresa.com"
                icon={Mail}
              />
            ) : (
              <ReadonlyInput
                value={client.correo}
                placeholder="correo@empresa.com"
                href={client.correo ? `mailto:${client.correo}` : undefined}
              />
            )}
          </Field>
          <Field id="telefono" label="Teléfono">
            {isEditing ? (
              <EditableInput
                value={formData.telefono}
                onChange={(v) => setField('telefono', v)}
                placeholder="+57 300 000 0000"
                icon={Phone}
              />
            ) : (
              <ReadonlyInput
                value={client.telefono}
                placeholder="+57 300 000 0000"
              />
            )}
          </Field>
        </div>
        <div className="mt-4">
          <Field id="direccion" label="Dirección">
            {isEditing ? (
              <EditableInput
                value={formData.direccion}
                onChange={(v) => setField('direccion', v)}
                placeholder="Dirección completa"
                icon={MapPin}
              />
            ) : (
              <ReadonlyInput
                value={client.direccion}
                placeholder="Dirección completa"
              />
            )}
          </Field>
        </div>
      </SectionCard>
    </motion.div>
  );
}



// ---------- componentes auxiliares ----------

function buildFormData(client) {
  if (!client) {
    return {
      estado: 'Activo',
      tipoEmpresa: '',
      nombre: '',
      nombreContacto: '',
      nit: '',
      correo: '',
      telefono: '',
      direccion: '',
    };
  }
  return {
    estado: client.estado || 'Activo',
    tipoEmpresa: client.tipoEmpresa || '',
    nombre: client.nombre || '',
    nombreContacto: client.nombreContacto || '',
    nit: client.nit || '',
    correo: client.correo || '',
    telefono: client.telefono || '',
    direccion: client.direccion || '',
  };
}

function isFormDirty(form, client) {
  if (!client) return true;
  return (
    (form.estado || '') !== (client.estado || 'Activo') ||
    (form.tipoEmpresa || '') !== (client.tipoEmpresa || '') ||
    (form.nombre || '').trim() !== (client.nombre || '') ||
    (form.nombreContacto || '') !== (client.nombreContacto || '') ||
    (form.nit || '').trim() !== (client.nit || '') ||
    (form.correo || '').trim() !== (client.correo || '') ||
    (form.telefono || '') !== (client.telefono || '') ||
    (form.direccion || '') !== (client.direccion || '')
  );
}

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4 border-b border-border">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          {Icon && <Icon className="h-4 w-4 text-primary" />}
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className="pt-5">{children}</CardContent>
    </Card>
  );
}

function Field({ id, label, required, badge, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label
          htmlFor={id}
          className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
        >
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
        {badge && (
          <Badge variant="outline" className="font-normal text-[10px] py-0 px-1.5">
            {badge}
          </Badge>
        )}
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

function EditableInput({ value, onChange, placeholder, type = 'text', icon: Icon, maxWidth, disabled }) {
  return (
    <>
      {Icon && (
        <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      )}
      <Input
        id={undefined}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(Icon && 'pl-9', maxWidth)}
        autoComplete="off"
      />
    </>
  );
}

function EditableSelect({ value, onChange, options, placeholder, disabled }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={disabled}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function ReadonlyInput({ value, placeholder, href, maxWidth }) {
  if (!value && !href) {
    return (
      <div
        className={cn(
          'flex h-10 w-full items-center rounded-md border border-dashed border-input bg-muted/30 px-3 text-sm text-muted-foreground italic',
          maxWidth,
        )}
      >
        {placeholder || '—'}
      </div>
    );
  }
  const display = value || placeholder;
  const className = cn(
    'flex h-10 w-full items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground',
    maxWidth,
  );
  if (href) {
    return (
      <a
        href={href}
        className={cn(
          className,
          'hover:bg-muted/60 hover:text-foreground transition-colors truncate',
        )}
      >
        {display}
      </a>
    );
  }
  return <div className={cn(className, 'truncate')}>{display}</div>;
}

function ReadonlySelect({ value, placeholder, options }) {
  if (!value) {
    return (
      <div className="flex h-10 w-full items-center rounded-md border border-dashed border-input bg-muted/30 px-3 text-sm text-muted-foreground italic">
        {placeholder || '—'}
      </div>
    );
  }
  const label = options?.find((o) => o.value === value)?.label || value;
  return (
    <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground truncate">
      {label}
    </div>
  );
}

function ReadonlyTextarea({ value, placeholder }) {
  if (!value) {
    return (
      <div className="flex min-h-[80px] w-full items-start rounded-md border border-dashed border-input bg-muted/30 px-3 py-2 text-sm text-muted-foreground italic">
        {placeholder || '—'}
      </div>
    );
  }
  return (
    <div className="flex min-h-[80px] w-full items-start rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-foreground whitespace-pre-wrap">
      {value}
    </div>
  );
}

function ComingSoon({ icon: Icon, title, hint }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card>
        <CardContent className="py-16 px-6">
          <div className="flex flex-col items-center text-center max-w-md mx-auto space-y-3">
            <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Icon className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{hint}</p>
            <Badge variant="outline" className="font-normal">
              Próximamente
            </Badge>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
